<template>
    <div class="page__content campsites">
        <h2 class="page__headline">{{campsites ? campsites.length : ''}} {{ $t('campsites.headline') }}</h2>
        <div class="columns">
            <div class="column is-8">
                <p class="page__copy">
                    {{ $t('campsites.copy') }}
                </p>
            </div>
        </div>
        <div class="columns">
            <div class="column is-12">
                <a class="button is-pulled-right" href="#" @click="collapseAll">{{ $t("generic.open_all_close_all") }}</a>
            </div>
        </div>
        <draggable v-bind="dragOptions" v-model="campsites" @start="onSortedStart" @end="onSortedEnd">
            <transition-group :name="sortTransitionName">
                <div class="campsites__campsite" v-for="(key, index) in campsites" :key="'campsite_'+key.id+index">
                    <campsite
                        :ref="`campsite-${index}`"
                        v-model="campsites[index]"
                        :seasons="seasons"
                        :suitables="suitables"
                        :allSuitables="allSuitables"
                        :allAttributes="allAttributes"
                        :attributeCategories="getCampsiteAttributes(key)"
                        :campsite-count=campsites.length
                        @del="openModal(index)"
                        @sort="setSort(key.id, $event)"
                        @update-attributes="updateAttributes"
                        @update-suitables="updateSuitables"
                        @copy-campsite="copyCampsite"
                    />
                </div>
            </transition-group>
        </draggable>
        <div class="is-clearfix">
            <a class="button is-primary is-pulled-left" @click="addCampsite()">{{ $t('campsites.addCampsite') }}</a>
            <router-link class="button is-pulled-right" exact :to="{ name: 'persons', params: {lang: lang} }" @click.native="nextStepClicked()">
                <span class="icon is-small">
                    <i class="fa fa-arrow-right" aria-hidden="true"></i>
                </span>
                <span>{{ $t('campsites.editPersons') }}</span>
            </router-link>
        </div>

        <div class="modal isActive" :class="{ 'is-active': modal.isActive }">
            <div class="modal-background" @click="closeModal()"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ modal.title }}</p>
                    <button class="delete" @click="closeModal()"></button>
                </header>
                <section class="modal-card-body" v-html="modal.content">
                </section>
                <footer class="modal-card-foot is-clearfix">
                    <a class="button" @click="closeModal()">{{ $t('generic.cancel') }}</a>
                    <a class="button is-danger" @click="deleteCampsite()">
                            <span class="icon">
                                <i class="fa fa-trash" aria-hidden="true"></i>
                            </span>
                        <span>{{ $t('generic.delete') }}</span>
                    </a>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import { cloneDeep } from 'lodash';

    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { Campsite } from '../../components';
    import { EventBus, Tracking } from '../../../utils';

    export default {
        name: 'campsites',

        components: {
            Campsite,
            draggable,
        },

        data() {
            return {
                isAllCollapsed: false,
                allSuitables: [],
                suitables: [],
                allAttributes: [],
                attributes: [],
                sortTransitionName: 'flip-list',
                dragOptions: {
                    handle: '.edit-box__dragger',
                    ghostClass: 'sortable-ghost',
                },
                campsites: null,
                seasons: AppModel.activeCampground.seasons,
                modal: {
                    isActive: false,
                    title: null,
                    content: null
                },
            };
        },

        computed: {
            lang() {
                return AppModel.lang;
            },
        },

        beforeRouteEnter(to, from, next) {
            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.campsites.fetchCampsites()]);
            }).then(function(response) {
                next(async(vm) => {
                    vm.campsites = response[1].body;
                    vm.attributes = (await Api.attributes.getActiveAccomodations()).data;
                    vm.allAttributes = (await Api.attributes.getAllAccomodations()).data;
                    vm.suitables = (await Api.suitables.getActiveSuitables()).data;
                    vm.allSuitables = (await Api.suitables.getAllSuitables()).data;
                });
            });
        },

        beforeDestroy() {
            this.deleteUnsavedCampsites();
        },

        created() {
        },

        methods: {
            collapseAll() {
                const collapseState = this.isAllCollapsed;

                this.isAllCollapsed = !this.isAllCollapsed;

                this.campsites.forEach((campsite, i) => {
                    this.$refs[`campsite-${i}`][0].isCollapsed = collapseState;
                });
            },
            updateAttributes(attributes) {
                this.attributes = this.attributes.map((category) => {
                    if(category.id === attributes.id) {
                        category.attributes = attributes.attributes;
                    }

                    return category;
                });

                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
            },
            async updateSuitables() {
                this.suitables = (await Api.suitables.getActiveSuitables()).data;

                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
            },
            getCampsiteAttributes(campsite) {
                return this.attributes.find((attrs) => attrs.id === campsite.id);
            },
            async copyCampsite() {
                this.campsites = (await Api.campsites.fetchCampsites()).data;
                this.attributes = (await Api.attributes.getActiveAccomodations()).data;
                this.allAttributes = (await Api.attributes.getAllAccomodations()).data;
            },
            addCampsite() {
                let campsite = {
                    campsite_type: null,
                    description: null,
                    included_adults: null,
                    included_children: null,
                    included_pets: null,
                    max_persons: null,
                    min_persons: null,
                    active: false,
                    cancellation_term_id: null,
                    max_pets: null,
                    name: null,
                    prices: [],
                    quantity: null,
                    arrival_weekdays: {
                        0: true,
                        1: true,
                        2: true,
                        3: true,
                        4: true,
                        5: true,
                        6: true,
                    },
                    departure_weekdays: {
                        0: true,
                        1: true,
                        2: true,
                        3: true,
                        4: true,
                        5: true,
                        6: true,
                    },
                };
                this.campsites.push(campsite);
                // scroll to position of added element which in this case is end of page
                setTimeout(() => {
                    document.body.scrollTop = this.$el.scrollHeight;
                }, 0);

                Tracking.trackEvent('Campsites', 'addCampsite', null, null);
            },

            deleteCampsite() {
                Api.campsites.deleteCampsite(this.modal.campsite)
                    .then((response) => {
                        if(Math.floor(response.status / 100) === 2) {
                            this.campsites.splice(this.modal.index, 1);
                            Api.bootstrap(true);
                            EventBus.$emit(EventBus.UPDATE_CHECKS);
                            Tracking.trackEvent('Campsites', 'delete', 'success', null);
                        } else {
                            console.error('delete failed', response);
                            EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                                title: 'Der Stellplatz konnte nicht gelöscht werden',
                                body: response.body.message,
                                type: 'danger',
                                direction: 'Up',
                                duration: 5000,
                            });
                            Tracking.trackEvent('Campsites', 'delete', 'failed', null);
                        }
                    }, (response) => {
                        console.error('delete failed', response);
                        Tracking.trackEvent('Campsites', 'delete', 'failed', null);
                    });
                this.closeModal();
            },

            closeModal() {
                this.modal.isActive = false;
            },

            openModal(index) {
                if(!this.campsites[index].id) {
                    this.campsites.splice(index, 1);
                    Tracking.trackEvent('Campsites', 'discardUnsaved', null, null);
                    return;
                }
                // if season is saved season, show warning modal
                this.modal.title = this.$t('generic.warning');
                this.modal.content = this.$t('generic.modal.delete', { name: this.campsites[index].name });
                this.modal.isActive = true;
                this.modal.campsite = this.campsites[index];
                this.modal.index = index;
            },

            onSortedStart() {
                this.sortTransitionName = 'flip-list-inactive';
            },

            onSortedEnd() {
                this.sortTransitionName = 'flip-list';
                let ordering = [];
                this.campsites.forEach(function(element) {
                    ordering.push(element.id);
                });
                Api.campsites.oderCampsite({ 'order': ordering })
                    .then((response) => {
                        if(response.ok) {
                            Tracking.trackEvent('Campsites', 'sort', 'success', null);
                        } else {
                            console.warn('order update failed');
                            Tracking.trackEvent('Campsites', 'sort', 'failed', null);
                        }
                    }, () => {
                        console.info('order update failed');
                        Tracking.trackEvent('Campsites', 'sort', 'failed', null);
                    });
            },

            deleteUnsavedCampsites() {
                for(let campsite in this.campsites) {
                    if(!this.campsites[campsite].id) {
                        this.campsites.splice(campsite, 1);
                        Tracking.trackEvent('Campsites', 'discardUnsavedOnLeave', null, null);
                    }
                }
            },

            nextStepClicked() {
                Tracking.trackEvent('Campsites', 'nextStep', 'persons', null);
            },

            setSort(cid, to) {
                const savedCampsites = cloneDeep(this.campsites);
                let campsite = null;
                let oldIdx = 0;
                let idx = 0;
                let newIdx = 0;
                for(let c of savedCampsites) {
                    if(c.id === cid) {
                        campsite = c;
                        oldIdx = idx;
                    }
                    idx++;
                }

                let dir = -1;

                if(to === 'up') {
                    newIdx = Math.max(0, oldIdx - 1);
                } else if(to === 'down') {
                    dir = 1;
                    newIdx = Math.min(savedCampsites.length - 1, oldIdx + 1);
                } else if(to === 'top') {
                    newIdx = 0;
                } else if(to === 'bottom') {
                    dir = 1;
                    newIdx = savedCampsites.length - 1;
                } else {
                    console.warn(`Illegal value for param 'to': '${to}'!`);
                    return;
                }

                if(newIdx === oldIdx) {
                    return;
                }

                if(campsite === null || campsite === undefined) {
                    console.warn(`Campsite with id '${cid}' not found!`);
                    return;
                }
                this.campsites = [];
                idx = 0;
                for(let c of savedCampsites) {
                    if(dir === -1 && idx === newIdx) {
                        this.campsites.push(campsite);
                    }
                    if(c.id !== cid) {
                        this.campsites.push(c);
                    }
                    if(dir === 1 && idx === newIdx) {
                        this.campsites.push(campsite);
                    }
                    idx++;
                }
                this.onSortedEnd();
            },
        }
    };
</script>

<style lang="scss">
    @import 'campsites.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/campsites/campsites.vue